<script lang="ts">
  import type { PlayerView, ReadyCheckView } from '../../shared/protocol';
  import { readyCheckElapsedMs, readyTimingStyle, type ReadyPrompt } from '../app/ready-check';
  import Button from '../ui/Button.svelte';
  import Dialog from '../ui/Dialog.svelte';

  interface Props {
    /** Guests confirm; the host waits with Cancel and Start; null closes the prompt. */
    prompt: ReadyPrompt;
    readyCheck: Readonly<ReadyCheckView> | null;
    /** The room's members; the host's prompt lists the guests who have confirmed. */
    players: readonly Readonly<PlayerView>[];
    /** Server milliseconds now, so a late-opened prompt shows the remaining undimmed share. */
    serverNow: () => number;
    confirm: () => void;
    cancel: () => void;
    start: () => void;
  }
  let { prompt, readyCheck, players, serverNow, confirm, cancel, start }: Props = $props();

  // One confirmation per check: the button settles until the server dismisses the prompt.
  let confirmedEndAt = $state<number | null>(null);
  const open = $derived(prompt !== null && readyCheck !== null);
  const confirmed = $derived(readyCheck !== null && confirmedEndAt === readyCheck.endAt);
  const elapsedMs = $derived(readyCheck ? readyCheckElapsedMs(readyCheck, serverNow()) : 0);
  const timing = $derived(readyTimingStyle(elapsedMs));
  const readyPlayers = $derived(players.filter((player) => player.ready === true && !player.host));

  function handleConfirm(): void {
    if (!readyCheck || confirmed) return;
    confirmedEndAt = readyCheck.endAt;
    confirm();
  }
</script>

<Dialog {open} required labelledby="ready-check-title">
  {#if prompt === 'host'}
    <h2 id="ready-check-title">Waiting for players</h2>
    {#if readyPlayers.length}
      <ul class="ready-list" aria-label="Ready players">
        {#each readyPlayers as player (player.id)}
          <li><span aria-hidden="true">{player.avatar}</span> {player.name}</li>
        {/each}
      </ul>
    {:else}
      <p class="ready-empty">No one is ready yet.</p>
    {/if}
    <div class="row">
      <Button onclick={cancel}>Cancel</Button>
      <!-- svelte-ignore a11y_autofocus: Start is the host's primary action while waiting. -->
      <Button variant="primary" autofocus onclick={start}>Start</Button>
    </div>
  {:else}
    <h2 id="ready-check-title">Are you ready?</h2>
    {#key readyCheck?.endAt}
      <!-- svelte-ignore a11y_autofocus: the prompt has one action and no other content to read. -->
      <Button
        variant="primary"
        size="big"
        full
        class="ready-button"
        autofocus
        disabled={confirmed}
        style={timing}
        onclick={handleConfirm}>Ready!</Button
      >
    {/key}
  {/if}
</Dialog>

<style>
  h2 {
    margin: 0 0 20px;
    text-align: center;
  }
  .row {
    display: flex;
    gap: 12px;
    justify-content: center;
  }
  .row :global(.btn) {
    flex: 1;
  }
  .ready-list {
    margin: 0 0 20px;
    padding-left: 22px;
  }
  .ready-list li {
    margin: 4px 0;
  }
  .ready-empty {
    margin: 0 0 20px;
    text-align: center;
    color: var(--theme-muted-text);
  }
</style>
